import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  KiAnbieterEinstellung,
  KiAnbieterTyp,
  KiFunktionen,
  KiMandantEinstellungen,
  KiRegion,
  KiRegionenFreigabe,
} from '../../models/ki.model';
import { KiEinstellungenService } from '../../services/ki-einstellungen.service';
import { KiMarkComponent } from '../../components/ki/ki-mark/ki-mark.component';
import { KiSwitchComponent } from '../../components/ki/ki-switch/ki-switch.component';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { KiProjektuebersichtComponent } from './ki-projektuebersicht/ki-projektuebersicht.component';
import { KiAuditLogComponent } from './ki-audit-log/ki-audit-log.component';
import { KiMaskierungComponent } from './ki-maskierung/ki-maskierung.component';
import { KiRasterAenderung, KiRasterComponent, kontingentGueltig } from './ki-raster/ki-raster.component';

const REGIONEN: readonly KiRegionenFreigabe[] = ['NUR_EU', 'EU_UND_DRITTLAENDER', 'NUR_LOKAL'];

/** Gleiche Zuordnung wie KiRegionenFreigabe im Backend, das sonst mit KI_ANBIETER_REGION ablehnt. */
const FREIGEGEBENE_REGIONEN: Record<KiRegionenFreigabe, readonly KiRegion[]> = {
  NUR_EU: ['EU', 'LOKAL'],
  EU_UND_DRITTLAENDER: ['EU', 'DRITTLAND', 'LOKAL'],
  NUR_LOKAL: ['LOKAL'],
};

interface KiAnbieterAnzeige {
  id: KiAnbieterTyp;
  host: string;
  /** Leer beim eigenen Endpunkt: dessen Region folgt erst aus der Adresse im Projekt. */
  region: KiRegion | null;
}

const ANBIETER: readonly KiAnbieterAnzeige[] = [
  { id: 'OPENAI', host: 'api.openai.com', region: 'DRITTLAND' },
  { id: 'AZURE_OPENAI', host: 'spirit.openai.azure.com', region: 'EU' },
  { id: 'MISTRAL', host: 'api.mistral.ai', region: 'EU' },
  { id: 'OLLAMA', host: 'ollama.intern:11434', region: 'LOKAL' },
  { id: 'EIGENER_ENDPUNKT', host: '', region: null },
];

const FUNKTIONEN: readonly (keyof KiFunktionen)[] = ['testdesign', 'pipelineAnalyse', 'mcp'];

const PFEIL_SCHRITT: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/** Reiter „KI" der Einstellungen (page-ki-einstellungen.jsx:43-198). */
@Component({
  selector: 'app-ki-mandant-section',
  standalone: true,
  imports: [
    A11yModule,
    TranslatePipe,
    MatButton,
    MatProgressSpinnerModule,
    KiMarkComponent,
    KiSwitchComponent,
    KiProjektuebersichtComponent,
    KiAuditLogComponent,
    KiMaskierungComponent,
    KiRasterComponent,
  ],
  templateUrl: './ki-mandant-section.component.html',
  styleUrl: './ki-mandant-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onAktivierenAbgebrochen()' },
})
export class KiMandantSectionComponent implements OnInit {
  private service = inject(KiEinstellungenService);

  readonly firmenname = input.required<string>();

  readonly regionen = REGIONEN;
  readonly anbieter = ANBIETER;
  readonly funktionen = FUNKTIONEN;
  readonly endpunktMax = FIELD_LENGTHS.URL_MAX;

  readonly laedt = signal(true);
  readonly ladefehler = signal(false);
  readonly bestaetigungOffen = signal(false);
  private readonly gespeichert = signal<KiMandantEinstellungen | null>(null);
  private readonly entwurf = signal<KiMandantEinstellungen | null>(null);
  /** Gründe des Backends je Musterwert, damit sie beim Entfernen anderer Zeilen an ihrem Muster bleiben. */
  readonly musterMeldungen = signal<ReadonlyMap<string, string>>(new Map());

  readonly aktiv = computed(() => this.entwurf()?.aktiv ?? false);
  readonly region = computed(() => this.entwurf()?.regionen ?? 'NUR_EU');
  readonly auditInhalte = computed(() => this.entwurf()?.auditInhalte ?? false);
  readonly eigeneMuster = computed(() => this.entwurf()?.eigeneMuster ?? []);
  readonly kontingent = computed(() => this.entwurf()?.kontingentTokensProMonat ?? null);
  readonly aufbewahrung = computed(() => this.entwurf()?.maxAufbewahrungTage ?? 90);
  private readonly maskierung = viewChild(KiMaskierungComponent);
  /** Getippt, aber nicht hinzugefügt: nur für die Rückfrage beim Verlassen, nicht zum Speichern. */
  readonly hatEntwurf = computed(() => this.maskierung()?.hatEntwurf() ?? false);
  readonly istGeaendert = computed(() => {
    const entwurf = this.entwurf();
    return JSON.stringify(entwurf && mitBereinigtenEndpunkten(entwurf)) !== JSON.stringify(this.gespeichert());
  });

  /** Vom Backend gemeldete Muster sperren nicht: Die Entscheidung trifft das Backend beim Speichern mit Grund. */
  readonly istGueltig = computed(
    () => kontingentGueltig(this.kontingent()) && !(this.maskierung()?.eingabeUngueltig() ?? false),
  );

  ngOnInit(): void {
    this.service.laden().subscribe({
      next: (einstellungen) => {
        this.uebernehmen(einstellungen);
        this.laedt.set(false);
      },
      error: () => {
        this.ladefehler.set(true);
        this.laedt.set(false);
      },
    });
  }

  onHauptschalter(an: boolean): void {
    if (an) {
      this.bestaetigungOffen.set(true);
    } else {
      this.entwurf.update((e) => e && { ...e, aktiv: false });
    }
  }

  onAktivierenBestaetigt(): void {
    this.entwurf.update((e) => e && { ...e, aktiv: true });
    this.bestaetigungOffen.set(false);
  }

  onAktivierenAbgebrochen(): void {
    this.bestaetigungOffen.set(false);
  }

  onDialogKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key === 'Escape') this.onAktivierenAbgebrochen();
  }

  onRegion(regionen: KiRegionenFreigabe): void {
    const gespeichert = this.gespeichert()?.anbieter ?? [];
    this.entwurf.update(
      (e) =>
        e && {
          ...e,
          regionen,
          anbieter: e.anbieter.map((a) => {
            if (anbieterGesperrt(regionen, a.id)) return { ...a, erlaubt: false };
            // Nur die vorige Region hat den Anbieter ausgeschaltet, nicht der Nutzer: gespeicherten Stand zurückholen.
            if (anbieterGesperrt(e.regionen, a.id)) {
              return { ...a, erlaubt: gespeichert.find((g) => g.id === a.id)?.erlaubt ?? false };
            }
            return a;
          }),
        },
    );
  }

  onRegionTaste(event: KeyboardEvent, index: number): void {
    const ziel = zielRegion(event.key, index);
    if (!ziel) return;
    event.preventDefault();
    this.onRegion(ziel);
    const gruppe = (event.currentTarget as HTMLElement).parentElement;
    gruppe?.querySelector<HTMLElement>(`[data-region="${ziel}"]`)?.focus();
  }

  anbieterEinstellung(id: KiAnbieterTyp): KiAnbieterEinstellung {
    return this.entwurf()!.anbieter.find((a) => a.id === id)!;
  }

  istGesperrt(id: KiAnbieterTyp): boolean {
    return anbieterGesperrt(this.region(), id);
  }

  onAnbieter(id: KiAnbieterTyp, erlaubt: boolean): void {
    this.anbieterAendern(id, { erlaubt });
  }

  onEndpunkt(event: Event): void {
    this.anbieterAendern('EIGENER_ENDPUNKT', { endpunkt: (event.target as HTMLInputElement).value });
  }

  funktionAn(name: keyof KiFunktionen): boolean {
    return this.entwurf()?.funktionen[name] ?? false;
  }

  onFunktion(name: keyof KiFunktionen, an: boolean): void {
    this.entwurf.update((e) => e && { ...e, funktionen: { ...e.funktionen, [name]: an } });
  }

  onAenderung(aenderung: KiRasterAenderung | Pick<KiMandantEinstellungen, 'eigeneMuster'>): void {
    this.entwurf.update((e) => e && { ...e, ...aenderung });
  }

  /** Ohne eigene Meldung, damit die Einstellungsseite eine gemeinsame Meldung für alle Reiter zeigt. */
  speichern$(): Observable<void> {
    const entwurf = this.entwurf();
    if (!entwurf) return of(undefined);
    return this.service.speichern(mitBereinigtenEndpunkten(entwurf)).pipe(
      tap((einstellungen) => this.uebernehmen(einstellungen)),
      map(() => undefined),
      catchError((fehler: HttpErrorResponse) => {
        this.musterAbgelehnt(entwurf.eigeneMuster, fehler);
        return throwError(() => fehler);
      }),
    );
  }

  zuruecksetzen(): void {
    this.entwurf.set(this.gespeichert());
    this.maskierung()?.zuruecksetzen();
  }

  private uebernehmen({ musterHinweise, ...einstellungen }: KiMandantEinstellungen): void {
    const vollstaendig = { ...einstellungen, anbieter: alleAnbieter(einstellungen.anbieter) };
    this.gespeichert.set(vollstaendig);
    this.entwurf.set(vollstaendig);
    this.musterMeldungen.set(
      new Map((musterHinweise ?? []).map((h) => [einstellungen.eigeneMuster[h.index], h.grund])),
    );
  }

  private musterAbgelehnt(gesendet: string[], fehler: HttpErrorResponse): void {
    const { code, index, error: grund } = fehler.error ?? {};
    if (code !== 'KI_MUSTER_UNGUELTIG' || typeof index !== 'number' || gesendet[index] === undefined) return;
    this.musterMeldungen.update((m) => new Map(m).set(gesendet[index], grund));
  }

  private anbieterAendern(id: KiAnbieterTyp, aenderung: Partial<KiAnbieterEinstellung>): void {
    this.entwurf.update(
      (e) => e && { ...e, anbieter: e.anbieter.map((a) => (a.id === id ? { ...a, ...aenderung } : a)) },
    );
  }
}

/** Tastatur der Radiogruppe nach dem APG-Muster: Pfeile springen im Kreis, Home/End an den Rand. */
function zielRegion(taste: string, index: number): KiRegionenFreigabe | undefined {
  if (taste === 'Home') return REGIONEN[0];
  if (taste === 'End') return REGIONEN[REGIONEN.length - 1];
  const schritt = PFEIL_SCHRITT[taste];
  return schritt ? REGIONEN[(index + schritt + REGIONEN.length) % REGIONEN.length] : undefined;
}

function anbieterGesperrt(regionen: KiRegionenFreigabe, id: KiAnbieterTyp): boolean {
  const region = ANBIETER.find((a) => a.id === id)?.region;
  return !!region && !FREIGEGEBENE_REGIONEN[regionen].includes(region);
}

/** Das Backend verlangt beim Speichern jeden Anbieter genau einmal; fehlende gelten als nicht erlaubt. */
function alleAnbieter(geladen: KiAnbieterEinstellung[]): KiAnbieterEinstellung[] {
  return ANBIETER.map(({ id }) => geladen.find((a) => a.id === id) ?? { id, erlaubt: false, endpunkt: null });
}

/** Getrimmt wird erst beim Speichern, sonst springt beim Tippen eines Leerzeichens Inhalt und Cursor im Feld. */
function mitBereinigtenEndpunkten(einstellungen: KiMandantEinstellungen): KiMandantEinstellungen {
  return {
    ...einstellungen,
    anbieter: einstellungen.anbieter.map((a) => ({ ...a, endpunkt: a.endpunkt?.trim() || null })),
  };
}
